// MyKvkkRequestPage — member-facing self-service for KVKK deletion (S13, Agent U).
//
// BE: POST /api/v1/me/kvkk-deletion-request          { reason }
//     POST /api/v1/me/kvkk-deletion-request/cancel   { reason }
//
// The authenticated user can request their own deletion or cancel a pending
// request. There is no member-side "get current request" endpoint, so the page
// drives state from mutation results only.

import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Space,
  Typography,
} from 'antd';
import { ShieldX } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { kvkkApi } from '@api/kvkk';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { recordAuditAction } from '@features/audit/recordAction';
import { useAuthStore } from '@features/auth/store';

const { Text, Paragraph } = Typography;

export function MyKvkkRequestPage() {
  const { t } = useTranslation(['kvkk', 'common', 'validation', 'errors']);
  const user = useAuthStore((s) => s.user);
  const [requestOpen, setRequestOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [submittedRequestId, setSubmittedRequestId] = useState<string | null>(
    null,
  );

  const requestMutation = useMutation({
    mutationFn: async () => {
      const req = await kvkkApi.create({
        appUserId: user?.id ?? '',
        reason: reason.trim() || undefined,
      });
      return req;
    },
    onSuccess: (req) => {
      setSubmittedRequestId(req.id);
      toast.success(t('mine.toast.requested'));
      void recordAuditAction({
        action: 'kvkk.selfRequest',
        resource: 'kvkk',
        resourceId: req.id,
        summary: t('mine.audit.selfRequest'),
        meta: { reason },
      });
      setRequestOpen(false);
      setReason('');
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const cancelMutation = useMutation({
    mutationFn: () => kvkkApi.cancel(submittedRequestId ?? '', cancelReason.trim() || undefined),
    onSuccess: () => {
      toast.success(t('mine.toast.cancelled'));
      setSubmittedRequestId(null);
      setCancelOpen(false);
      setCancelReason('');
      void recordAuditAction({
        action: 'kvkk.selfCancel',
        resource: 'kvkk',
        summary: t('mine.audit.selfCancel'),
        meta: { reason: cancelReason },
      });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  return (
    <div>
      <PageHeader
        title={t('mine.page.title')}
        description={t('mine.page.description')}
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: t('mine.breadcrumb.account') },
          { label: t('mine.breadcrumb.self') },
        ]}
      />

      <Card>
        <Paragraph>
          {t('mine.introBefore')}
          <strong>{t('mine.introStrong')}</strong>
          {t('mine.introAfter')}
        </Paragraph>

        {submittedRequestId ? (
          <Alert
            type="warning"
            showIcon
            message={t('mine.pending.message')}
            description={
              <div>
                <Text>
                  {t('mine.pending.requestId')} <code>{submittedRequestId}</code>
                </Text>
                <div style={{ marginTop: 8 }}>
                  <Button
                    danger
                    onClick={() => setCancelOpen(true)}
                  >
                    {t('mine.pending.cancelButton')}
                  </Button>
                </div>
              </div>
            }
          />
        ) : (
          <Space direction="vertical" size={12}>
            <Alert
              type="info"
              showIcon
              message={t('mine.idle.message')}
            />
            <Button
              danger
              icon={<ShieldX size={14} />}
              onClick={() => setRequestOpen(true)}
            >
              {t('mine.idle.createButton')}
            </Button>
          </Space>
        )}
      </Card>

      <Modal
        title={t('mine.requestModal.title')}
        open={requestOpen}
        onCancel={() => setRequestOpen(false)}
        okText={t('mine.requestModal.ok')}
        okButtonProps={{ danger: true }}
        confirmLoading={requestMutation.isPending}
        cancelText={t('common:actions.cancel')}
        onOk={() => requestMutation.mutate()}
      >
        <Alert
          type="warning"
          showIcon
          message={t('mine.requestModal.message')}
          description={t('mine.requestModal.description')}
          style={{ marginBottom: 12 }}
        />
        <div style={{ marginBottom: 8, fontSize: 13 }}>
          {t('mine.requestModal.reasonLabel')}
        </div>
        <Input.TextArea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          showCount
          placeholder={t('mine.requestModal.reasonPlaceholder')}
        />
      </Modal>

      <Modal
        title={t('mine.cancelModal.title')}
        open={cancelOpen}
        onCancel={() => setCancelOpen(false)}
        okText={t('mine.cancelModal.ok')}
        confirmLoading={cancelMutation.isPending}
        cancelText={t('common:actions.cancel')}
        onOk={() => cancelMutation.mutate()}
      >
        <Alert
          type="info"
          showIcon
          message={t('mine.cancelModal.message')}
          style={{ marginBottom: 12 }}
        />
        <div style={{ marginBottom: 8, fontSize: 13 }}>
          {t('mine.cancelModal.reasonLabel')}
        </div>
        <Input.TextArea
          rows={3}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          maxLength={500}
          showCount
        />
      </Modal>
    </div>
  );
}
